<template>
  <button
    class="playlist-indicator"
    :class="{ visible: visible }"
    role="button"
    tabindex="0"
    :aria-label="label"
    @click.stop="emit('toggle')"
    @keydown.enter.stop="emit('toggle')"
    @keydown.space.prevent.stop="emit('toggle')"
  >
    <span class="playlist-indicator__icon" v-html="playlistIcon" aria-hidden="true"></span>
    <span class="playlist-indicator__text">{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { playlistIcon } from '../../assets/icons'
import { useVideoI18n } from '../../i18n'

/**
 * "n / total" playlist pill. Player-agnostic so the collection shell can show
 * it above a third-party item, which has no video player behind it.
 */
interface Props {
  visible?: boolean
  /** 0-based index of the current item */
  currentIndex: number
  totalItems: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  toggle: []
}>()

const { t } = useVideoI18n()

const label = computed(() =>
  t('player.homepage.playlistIndicator', {
    current: props.currentIndex + 1,
    total: props.totalItems,
  }),
)
</script>

<style scoped lang="scss">
@use '../../styles/mixins' as *;

.playlist-indicator {
  position: absolute;
  top: 1rem;
  left: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  cursor: pointer;
  z-index: 15;
  // Mounted while hidden: blurred only while shown, and no fade under reduced motion.
  @include blur-hidden;
  font-family: inherit;
  white-space: nowrap;

  &.visible {
    @include blur-shown;
  }

  &:hover {
    background: var(--vp-backdrop-medium);
  }

  &:focus-visible {
    outline: 2px solid var(--vp-primary-color, #007bff);
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    top: 0.5rem;
    left: 0.5rem;
    padding: 0.375rem 0.625rem;
    gap: 0.375rem;
  }

  &__icon {
    display: flex;
    width: 20px;
    height: 20px;
    flex-shrink: 0;

    :deep(svg) {
      width: 100%;
      height: 100%;
    }

    @media (max-width: 768px) {
      width: 18px;
      height: 18px;
    }
  }

  &__text {
    font-size: 0.875rem;
    font-weight: 600;
    line-height: 1;

    @media (max-width: 768px) {
      font-size: 0.8125rem;
    }
  }
}

// Light theme — over video, but use backdrop tokens for consistency
[data-vp-theme='light'] .playlist-indicator {
  background: var(--vp-backdrop-medium);
  color: var(--vp-overlay-text-color);

  &:hover {
    background: var(--vp-backdrop-heavy);
  }
}
</style>
